import React, { useEffect, useState } from 'react';
import { Collapse, Spin, Tag, Typography, Alert } from 'antd';
import { getCoverage, type CoverageResult } from '../../services/adminService';

const { Text } = Typography;

interface CoverageListProps {
  group: string | null | undefined;
  id: number | null | undefined;
  // Render expanded (provision preview) or collapsed (SIM page).
  defaultOpen?: boolean;
  title?: string;
}

// KAN-167: the countries a roaming profile covers, from the mirrored zone map.
const CoverageList: React.FC<CoverageListProps> = ({ group, id, defaultOpen = false, title }) => {
  const [data, setData] = useState<CoverageResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!group || id == null) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getCoverage(group, id, controller.signal)
      .then(res => setData(res))
      .catch(err => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load coverage');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [group, id]);

  if (!group || id == null) return null;

  const header =
    title ||
    (data
      ? data.available
        ? `Coverage: ${data.countries.length} ${data.countries.length === 1 ? 'country' : 'countries'}`
        : 'Coverage'
      : 'Coverage');

  return (
    <Collapse
      size="small"
      defaultActiveKey={defaultOpen ? ['c'] : []}
      items={[
        {
          key: 'c',
          label: header,
          children: loading ? (
            <Spin size="small" />
          ) : error ? (
            <Alert type="warning" showIcon message={error} />
          ) : data && !data.available ? (
            <Text type="secondary">
              {data.note || 'No country list available for this profile.'}
            </Text>
          ) : data && data.countries.length ? (
            <div>
              {data.tier != null && data.tier > 1 && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Tier {data.tier} includes every country in zones 1 to {data.tier} of the{' '}
                  {data.region} region.
                </Text>
              )}
              {data.countries.map(c => (
                <Tag key={c} style={{ marginBottom: 4 }}>
                  {c}
                </Tag>
              ))}
            </div>
          ) : (
            <Text type="secondary">No countries listed.</Text>
          ),
        },
      ]}
    />
  );
};

export default CoverageList;
